






import Button from '@/types/components/Button.interface'
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import IListTile from '@/types/components/IListTile.interface'
import User from '@/types/User.interface'
import Vue from 'vue'
export default Vue.extend({
    data() {
        return {
            //  Determines if we have finished mounting
            mounted: false,
            toolbar: { title: 'Settings' }
        }
    },
    async mounted() {
        //  Check if the 'create' flag is set, AND the tenant does NOT have a data provider
        const needsProvider =
            this.$route.query.create && !this.$store.getters.tenant.data_provider

        //  Check if the workspace flag is set
        const popWorkspace =
            this.$route.query.open && this.$route.query.open === 'workspace'

        //  Check if the inbox flag is set
        const popInbox = this.$route.query.open && this.$route.query.open === 'inbox'

        //  If the tenant needs a provider, go to dataprovider select
        if (needsProvider) this.dataProviderSelect()

        //  If the tenant wants to open workspace, open it
        if (popWorkspace) this.openWorkspaceSettings()

        //  If the tenant wants to open inbox settings, open it
        if (popInbox) this.openInboxSettings()

        //  Set mounted to true now that we are done
        this.mounted = true
    },
    computed: {
        inboxSettingsEnabled(): boolean {
            return this.$store.getters.getEnabled('inbox_setting')
        },
        items(): Button[] {
            //  Reference to our store function for checking user permissions
            const hasPermission = this.$store.getters.userHasPermission

            //  Create our array of items
            const items = [
                {
                    id: 0,
                    text: 'Account and Phone Settings',
                    icon: 'mdi-account-circle',
                    callback: () => this.goToLink('/account'),
                    disabledPopup: !hasPermission(0)
                },
                {
                    id: 1,
                    text: 'Subscription Settings',
                    icon: 'mdi-ballot',
                    callback: () => this.goToLink('/billing/subscription'),
                    disabledPopup: !hasPermission(0),
                    hidden: this.$store.getters.isApp || this.resellerBlock
                },
                {
                    id: 2,
                    text: 'Team',
                    icon: 'mdi-contacts',
                    callback: () => this.goToLink('/teammates')
                },
                {
                    id: 3,
                    text: 'Notification Settings',
                    icon: 'mdi-bell',
                    callback: () => this.goToLink('/notifications?settings=true'),
                    disabledPopup: !hasPermission(0)
                },
                {
                    id: 4,
                    text: 'Inbox Settings',
                    icon: 'mdi-inbox-full-outline',
                    callback: () => this.openInboxSettings(),
                    disabledPopup: !hasPermission(2),
                    hidden: !this.inboxSettingsEnabled
                },
                {
                    id: 5,
                    text: 'Workspace Settings',
                    icon: 'bi-gear-wide-connected',
                    callback: () => this.openWorkspaceSettings(),
                    disabledPopup: !hasPermission(2)
                },
                {
                    id: 6,
                    text: 'Import and Export Contacts',
                    icon: 'mdi-database',
                    callback: () => this.goToLink('/csv/import'),
                    disabledPopup: !hasPermission(2),
                    hidden: this.$store.getters.isMobile() || this.$store.getters.isApp
                },
                {
                    id: 7,
                    text: 'Compliance Settings',
                    icon: 'mdi-message-lock',
                    callback: () => this.goToLink('/opt'),
                    disabledPopup: !hasPermission(2)
                },
                {
                    id: 8,
                    text: 'Message History',
                    icon: 'mdi-table-large',
                    callback: () => this.$router.push('/messages'),
                    disabledPopup: !hasPermission(1),
                    hidden: this.$store.getters.isMobile() || this.$store.getters.isApp
                },
                {
                    id: 9,
                    text: 'Message Signature',
                    icon: 'bi-pen-fill',
                    callback: () => this.goToLink('/signature'),
                    disabledPopup: !hasPermission(1)
                },
                // {
                //     id: 10,
                //     text: 'Refer Friends, Get Rewards',
                //     icon: 'bi-cash',
                //     callback: () => this.goToLink('/referral')
                // },
                {
                    id: 11,
                    text: 'Plugins',
                    icon: 'mdi-apps',
                    callback: () => this.goToLink('/plugins'),
                    disabledPopup: !hasPermission(1)
                },
                {
                    id: 12,
                    text: 'View Workspaces',
                    icon: 'mdi-domain',
                    callback: () => this.goToMemberships(),
                    disabledPopup: !hasPermission(3) || !this.multitenantEnabled
                },
                {
                    id: 13,
                    text: 'Migrate To Inbox by MessageDesk',
                    icon: 'mdi-folder-move',
                    disabledPopup: !hasPermission(0),
                    callback: () => this.migrateToV2()
                }
            ]

            //  Filter out the hidden items and permission blocked items
            return items.filter((x: Button) => x.hidden !== true)
        },
        multitenantEnabled(): boolean {
            //  Check if this user is a reseller, and if so, return true
            if (this.user.reseller) return true

            //  Grab the user memberships
            const memberships = this.user.memberships

            //  If they have more than 1, return true
            if (memberships.length > 1) return true

            //  Else, default to false
            return false
        },
        resellerBlock(): boolean {
            //  If we have no users, return false
            if (!this.$store.getters.users.length) return false

            //  Grab the user's role
            const role = this.$store.getters.userRole(this.user)

            //  If they are a support user, return false
            if (role.id > 4) return false

            //  Else, get the admin user id
            const adminId = this.$store.getters.tenant.admin_user_id

            //  If the current user is the admin, return false
            if (adminId === this.user.id) return false

            //  Else, grab the admin user based on id
            const admin = this.$store.getters.users.find((x: User) => x.id === adminId)

            //  Check if this admin is a reseller
            const reseller = admin.reseller

            //  If the admin is NOT a reseller, return false
            if (!reseller) return false

            //  Else, return true
            return true
        },
        user(): User {
            return this.$store.getters.user
        }
    },
    methods: {
        dataProviderSelect(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Choose Your Data Provider',
                icon: 'mdi-cable-data',
                color: 'primary',
                component: 'data-provider-popup',
                closeable: true
            })
        },
        goToLink(link: string): void {
            this.$router.push(link)
        },
        goToMemberships(): void {
            this.$store.commit('tenantSet', null)
            this.$router.push('/memberships')
        },
        goToDataProvider(item: Button): void {
            //  Close the app dialog
            this.$store.dispatch('dialogClose', 'APP')

            //  Determine which route to go to
            const route = item.text === 'QuickBooks Online' ? '/qbo' : '/csv/import'

            //  Navigate to the proper route
            this.$router.push(route)
        },
        migrateToV2(): void {
            const canMigrateToV2 = this.$store.getters.settingGet('canMigrateToV2')

            //  If user not allowed to migrate we offer them to join the waitlist instead
            if (!canMigrateToV2.enabled) {
                return this.popCannotMigrateToV2()
            }

            //  Otherwise we navigate them to the migration page
            this.goToLink('/migration')
        },
        openInboxSettings(): void {
            //  Check if we have access to inbox settings
            if (!this.inboxSettingsEnabled) return this.popInboxSettingsDisabled()

            //  Else, pop our settings
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                icon: 'bi-gear-wide-connected',
                color: 'primary',
                component: 'inbox-settings',
                closeable: true
            })
        },
        openWorkspaceSettings(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                icon: 'bi-gear-wide-connected',
                color: 'primary',
                component: 'workspace-settings',
                closeable: true
            })
        },
        popCannotMigrateToV2(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                icon: 'mdi-folder-move',
                color: 'primary',
                component: 'migration-popup',
                width: '650px',
                closeable: true
            })
        },
        popInboxSettingsDisabled(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'FEATURE',
                paywall_type: 'access',
                metadata: 'inbox_setting_enabled'
            })
        },
        settingIcon(item: Button): IListIcon {
            //  We will use the icon from our item
            const icon = item.icon

            //  Check if we are disabled
            const disabled = item.disabledPopup

            //  Color depends on if disabled or not
            const color = disabled ? 'rgba(0,0,0,0.26)' : 'primary'

            //  Create our list icon
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: color
            }

            //  Return our icon
            return listIcon
        },
        settingsTile(): IListTile {
            return {
                leftIcon: (item: Button): IListIcon => this.settingIcon(item),
                // img: (item) => item.img,
                line1: (item: Button): string => item.text,
                // method: 'goToLink'
                callback: (item: Button): void => item.callback()
            }
        }
    }
})
